import { createApp, h, type App } from 'vue'


import Dialog from '../components/elements/dialog/Dialog.vue'

interface DialogButton {
  text?: string
  type?: string
  variant?: string
  outline?: boolean
  flat?: boolean
  callback?: () => Promise<boolean> | boolean
  [key: string]: any
}

interface DialogOptions {
  title?: string
  confirm?: DialogButton
  cancel?: DialogButton | boolean
  icon?: string
  variant?: string
  container?: string | object | null
}

export interface DialogPlugin {
  create(message: string, options: DialogOptions): Promise<any>
  alert(message: string, options?: DialogOptions): Promise<any>
  confirm(message: string, options?: DialogOptions): Promise<any>
}

function destroyDialog(container: HTMLElement, app: App) {
  app.unmount()
  container.remove()
}

export default defineNuxtPlugin((nuxtApp) => {
  // Register ODialog as a global component
  nuxtApp.vueApp.component('ODialog', Dialog)

  const plugin: DialogPlugin = {
    create(message: string, { title, confirm, cancel, icon, variant, container }: DialogOptions = {}) {
      const mountContainer = document.createElement('div')
      document.body.appendChild(mountContainer)

      // Build the options array from confirm/cancel props
      const options: DialogButton[] = []
      if (confirm) {
        options.push({ type: 'confirm', ...confirm })
      }
      if (cancel) {
        const cancelOption = typeof cancel === 'boolean'
          ? { type: 'cancel' }
          : { type: 'cancel', ...cancel }
        options.push(cancelOption)
      }

      let dialogInstance: any = null
      let dialogApp: App | null = null

      dialogApp = createApp({
        setup() {
          return () => h(Dialog, {
            info: message,
            title,
            icon: icon || null,
            options,
            container,
            ref: 'dialog',
            onClose: () => {
              if (dialogApp && mountContainer) {
                destroyDialog(mountContainer, dialogApp)
              }
            },
          })
        },
      })

      // Share the parent app's global config so i18n, router, etc. are available
      dialogApp.config.globalProperties = nuxtApp.vueApp.config.globalProperties

      // Copy registered global components so Dialog can use o-button, o-icon, etc.
      const parentComponents = (nuxtApp.vueApp as any)._context?.components
      if (parentComponents) {
        Object.assign((dialogApp as any)._context.components, parentComponents)
      }

      const mounted = dialogApp.mount(mountContainer)
      dialogInstance = (mounted as any).$refs.dialog

      if (dialogInstance?.open) {
        return dialogInstance.open()
      }

      return Promise.resolve()
    },

    alert(message: string, options: DialogOptions = {}) {
      const { title, variant, icon, confirm, container } = options
      return this.create(message, { title, confirm, variant, icon, container })
    },

    confirm(message: string, options: DialogOptions = {}) {
      const { title, variant, icon, confirm, container } = options
      const cancel = Object.assign({ cancel: true }, options.cancel)

      return this.create(message, { title, cancel, confirm, variant, icon, container })
    },
  }

  nuxtApp.provide('dialog', plugin)
})
